/**
 * How guide entries look wherever they show (ADR-049, ADR-050): the guide tabs, My Stay's
 * welcome story and Getting Here pages, and the Help sheet. Rows open in place, cards are always
 * open, places have a photo with Map and More info.
 */
import { Chip, Icon, type IconName } from '@pmh/shared';
import { useState } from 'react';
import { GuideText, linkProps, mapsUrl } from '../lib/guide';
import type { GuideEntry, GuideGroup, GuideSection } from '../lib/types';
import { isNewsletterLink, NewsletterSheet } from './Newsletter';

export const TONES = ['rust', 'pine', 'mustard'] as const;

/** `plain`: a whole tab's intro, with no box, so it doesn't look like a category's (Rook, 2026-10-19). */
export function Overview({
  heading,
  intro,
  tip,
  plain = false,
}: {
  heading: string;
  intro: string;
  tip: string;
  plain?: boolean;
}) {
  if (!heading && !intro && !tip) return null;
  return (
    <div className={plain ? 'g-overview g-overview--plain' : 'g-overview'}>
      {heading && <h3 className="g-overview__title">{heading}</h3>}
      {intro && <GuideText text={intro} />}
      {tip && (
        <div className="g-overview__tip">
          <span className="g-kicker" style={{ color: 'var(--rust)' }}>
            Host tip
          </span>
          <GuideText text={tip} />
        </div>
      )}
    </div>
  );
}

/** More info and Map; Map is enough for an address (Rook, 2026-10-19: no Copy address). A link
 *  to Mailchimp's signup page opens the newsletter sheet in place, as V1 did (Rook, 2026-10-20). */
function Buttons({ e }: { e: GuideEntry }) {
  const [news, setNews] = useState(false);
  if (!e.address && !e.link) return null;
  return (
    <div className="g-entry__btns">
      {isNewsletterLink(e.link) ? (
        <>
          <button type="button" className="pmh-btn pmh-btn--primary pmh-btn--sm" aria-haspopup="dialog" onClick={() => setNews(true)}>
            {e.linkLabel || 'Subscribe'}
            <Icon name="arrowRight" size={16} />
          </button>
          <NewsletterSheet open={news} onClose={() => setNews(false)} />
        </>
      ) : e.link && (
        <a className="pmh-btn pmh-btn--primary pmh-btn--sm" {...linkProps(e.link)}>
          {e.linkLabel || 'More info'}
          <Icon name="arrowRight" size={16} />
        </a>
      )}
      {e.address && (
        <a
          className="pmh-btn pmh-btn--secondary pmh-btn--sm"
          {...linkProps(mapsUrl(e.address, e.title))}
        >
          <Icon name="pin" size={16} />
          Map
        </a>
      )}
    </div>
  );
}

/** What's inside an entry once it's open (or always, on a card). */
export function Details({ e }: { e: GuideEntry }) {
  return (
    <>
      {e.photo && <img className="g-entry__photo" src={e.photo} alt={e.title} loading="lazy" />}
      {e.address && <p className="g-entry__addr">{e.address}</p>}
      {e.body && <GuideText text={e.body} />}
      {e.tags.length > 0 && (
        <div className="pmh-chiprow">
          {e.tags.map((t) => (
            <Chip key={t} tone="cream">
              {t}
            </Chip>
          ))}
        </div>
      )}
      <Buttons e={e} />
    </>
  );
}

/** "Our fave" (a star) and "Guest fave" (a heart, Rook 2026-10-19); a place can have both. */
function Faves({ e }: { e: GuideEntry }) {
  if (!e.favorite && !e.guestFave) return null;
  return (
    <span className="g-faves">
      {e.favorite && (
        <span className="g-fave">
          <Icon name="star" size={14} /> Our fave
        </span>
      )}
      {e.guestFave && (
        <span className="g-fave g-fave--guest">
          <Icon name="heart" size={14} /> Guest fave
        </span>
      )}
    </span>
  );
}

export function EntryIcon({ name, tone }: { name: IconName; tone: number }) {
  return (
    <span className={`g-entry__icon g-entry__icon--${TONES[tone % 3]}`} aria-hidden="true">
      <Icon name={name} size={18} />
    </span>
  );
}

/** A tap-to-open row: rules, where to find things, how-tos. */
function Row({
  e,
  icon,
  tone,
  open,
  onToggle,
}: {
  e: GuideEntry;
  icon: IconName;
  tone: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <li className={`g-entry g-entry--row${open ? ' is-open' : ''}`} id={`e-${e.id}`}>
      <button type="button" className="g-entry__head" aria-expanded={open} onClick={onToggle}>
        <EntryIcon name={e.icon ?? icon} tone={tone} />
        <span className="g-entry__titles">
          <span className="g-entry__title">{e.title}</span>
          {e.where && (
            <span className="g-entry__where">
              <Icon name="pin" size={13} /> {e.where}
            </span>
          )}
        </span>
        <Faves e={e} />
        <Icon name="chevronDown" size={18} className="g-entry__chev" />
      </button>
      {open && (
        <div className="g-entry__body">
          {e.subtitle && <p className="g-entry__sub">{e.subtitle}</p>}
          <Details e={e} />
        </div>
      )}
    </li>
  );
}

/** An always-open card: welcome, arrival, help, feedback. */
/** `icon` null: only an icon the entry has itself (My Stay's pages, where one per card is noise). */
export function Card({ e, icon, tone }: { e: GuideEntry; icon: IconName | null; tone: number }) {
  const shown = e.icon ?? icon;
  return (
    <li className="g-entry g-entry--card" id={`e-${e.id}`}>
      <div className="g-entry__cardhead">
        {shown && <EntryIcon name={shown} tone={tone} />}
        <span className="g-entry__titles">
          <span className="g-entry__title">{e.title}</span>
          {e.subtitle && <span className="g-entry__sub">{e.subtitle}</span>}
          {e.where && (
            <span className="g-entry__where">
              <Icon name="pin" size={13} /> {e.where}
            </span>
          )}
        </span>
        <Faves e={e} />
      </div>
      <Details e={e} />
    </li>
  );
}

/** A place to go: photo, what it is, how far, Map and More info. */
function Place({ e }: { e: GuideEntry }) {
  return (
    <li className="g-entry g-entry--place" id={`e-${e.id}`}>
      {e.photo && <img className="g-entry__hero" src={e.photo} alt={e.title} loading="lazy" />}
      <div className="g-entry__placebody">
        <div className="g-entry__cardhead" style={{ alignItems: 'flex-start' }}>
          <span className="g-entry__titles">
            {e.subtitle && (
              <span className="g-kicker" style={{ color: 'var(--rust)' }}>
                {e.subtitle}
              </span>
            )}
            <span className="g-entry__title g-entry__title--lg">{e.title}</span>
            {e.where && (
              <span className="g-entry__where">
                <Icon name="pin" size={13} /> {e.where}
              </span>
            )}
          </span>
          <Faves e={e} />
        </div>
        <Details e={{ ...e, photo: null }} />
      </div>
    </li>
  );
}

export function Entries({
  section,
  group,
  gi,
  openId,
  setOpen,
}: {
  section: GuideSection;
  group: GuideGroup;
  gi: number;
  openId: string | null;
  setOpen: (id: string | null) => void;
}) {
  const icon = group.icon ?? section.icon;
  return (
    <ul className={`g-entries g-entries--${section.layout}`}>
      {group.entries.map((e) =>
        section.layout === 'places' ? (
          <Place key={e.id} e={e} />
        ) : section.layout === 'cards' || section.layout === 'pages' ? (
          <Card key={e.id} e={e} icon={icon} tone={gi} />
        ) : (
          <Row
            key={e.id}
            e={e}
            icon={icon}
            tone={gi}
            open={openId === e.id}
            onToggle={() => setOpen(openId === e.id ? null : e.id)}
          />
        ),
      )}
    </ul>
  );
}
